import { useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { Line } from "react-chartjs-2";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { LoadingSlideAtom } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { debouncedResize } from "@features/sales-v3/utils/chart";
import { formatting } from "@north-beam/nb-common";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";

import { usePerformanceTrackingChartView } from "../../hooks/use-chart-state";
import { usePerformanceTrackingLiveForecastData } from "../../hooks/use-performance-tracking-live-forecast";
import { usePerformanceTrackingSpendData } from "../../hooks/use-performance-tracking-spend-data";
import { useAllSelectedRowIds } from "../../table/contexts";
import { MetricSelector } from "../metric-selector";

import { ChartViewsButtons } from "./chart-views-buttons";
import { ACTUAL_ID, BUDGETED_ID, LIVE_FORECAST_ID } from "./const";
import { plugins } from "./plugins";
import { setChartData } from "./plugins/chart-data-manager-plugin";
import { TrackingChartLegend } from "./spend-chart-legend/tracking-chart-legend";
import {
  useBuildLiveForecastSpendDataset,
  useBuildSpendDataset,
  type PerformanceTrackingDataset,
} from "./spend-chart-legend/utils";
import { getTrackingChartOptions } from "./spend-chart-options";

const chartDatasets: any[] = [];

export function SpendChart() {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const { user } = useUser();

  const [datasetVisibility, setDatasetVisibility] = useState<
    Record<string, boolean>
  >({
    [ACTUAL_ID]: true,
    [BUDGETED_ID]: true,
    [LIVE_FORECAST_ID]: true,
  });

  const [chartView] = usePerformanceTrackingChartView();
  const isSplitView = chartView === "split";
  const { trackingSpendData, loading } = usePerformanceTrackingSpendData();
  const { trackingliveForecastData } = usePerformanceTrackingLiveForecastData();

  const [allSelectedRowIds] = useAllSelectedRowIds();
  const needsToSelectRows = isSplitView && allSelectedRowIds.length === 0;

  const { datasets, labels, dataColumnMappings } = useBuildSpendDataset({
    data: trackingSpendData,
    datasetVisibility,
  });

  const { datasets: forecastDatasets } = useBuildLiveForecastSpendDataset({
    data: trackingliveForecastData,
    datasetVisibility,
    dataColumnMappings,
  });

  const [chartRef, setChartRef] = useState<ChartJSOrUndefined>();

  useEffect(() => {
    chartRef && debouncedResize(chartRef);
  }, [chartRef]);

  useEffect(() => {
    const allDatasets = [
      ...datasets,
      ...(!isSplitView ? forecastDatasets : []),
    ];

    setChartData(chartRef, {
      data: { datasets: allDatasets, labels },
      datasetVisibility,
    });
  }, [
    chartRef,
    datasets,
    datasetVisibility,
    labels,
    forecastDatasets,
    isSplitView,
  ]);

  const setChartInstance = useCallback(
    (instance: ChartJSOrUndefined | null) => instance && setChartRef(instance),
    [],
  );

  const chartOptions = useMemo(
    () =>
      getTrackingChartOptions(
        (value: number) =>
          formatting.formatCurrency(value, {
            precision: "exact",
            code: user.currencies.currentCurrency,
          }),
        user.timezone,
      ),
    [user.currencies.currentCurrency, user.timezone],
  );

  return (
    <>
      {loading && (
        <div className="flex-center h-[496px]">
          <LoadingSlideAtom />
        </div>
      )}
      <div className={clsx(loading && "hidden", "flex flex-col space-y-4")}>
        <div className="flex justify-between">
          <MetricSelector />
          <ChartViewsButtons />
        </div>
        {needsToSelectRows && (
          <motion.div
            className="text-lg font-semibold flex-center h-[420px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeIn" }}
          >
            Please select rows to view the chart
          </motion.div>
        )}
        <div
          className={clsx("flex justify-between w-full items-center relative", {
            hidden: needsToSelectRows,
          })}
        >
          <div className="font-bold text-sm mb-2">
            Spend (cumulative and daily)
          </div>
          <TrackingChartLegend
            datasets={datasets as PerformanceTrackingDataset[]}
            datasetVisibility={datasetVisibility}
            setDatasetVisibility={(values) => {
              trackMMMEvent({
                component: TrackPerformanceTrackedComponent.SPEND_CHART,
                action: "onChange",
                actionInfo: "Change dataset visibility",
              });

              setDatasetVisibility(values);
            }}
          />
          <div />
        </div>
        <motion.div
          className={clsx("h-96", { hidden: needsToSelectRows })}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeIn" }}
        >
          <Line
            id="mmm-spend-chart"
            ref={setChartInstance}
            options={chartOptions}
            plugins={plugins}
            data={{ datasets: chartDatasets }}
          />
        </motion.div>
      </div>
    </>
  );
}
